import { useState, useEffect } from "react";
import { 
  ChevronDown, 
  ChevronRight, 
  FileText, 
  Plus, 
  Star,
  Home,
  Clock,
  Folder,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { TeamKnowledgeBaseEntry } from "@/types/team";

interface ConfluenceNavSidebarProps {
  entries: TeamKnowledgeBaseEntry[];
  categories: string[];
  selectedCategory: string | null;
  selectedEntryId: string | null;
  onCategorySelect: (category: string | null) => void;
  onEntrySelect: (entryId: string) => void;
  onCreateNew: () => void;
  canEdit: boolean;
}

export const ConfluenceNavSidebar = ({
  entries,
  categories,
  selectedCategory,
  selectedEntryId,
  onCategorySelect,
  onEntrySelect,
  onCreateNew,
  canEdit
}: ConfluenceNavSidebarProps) => {
  const STORAGE_KEY = 'knowledge-base-expanded-categories';
  
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set(categories);
      }
    }
    return new Set(categories);
  });

  useEffect(() => {
    // Add new categories that aren't saved yet
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setExpandedCategories(new Set(categories));
    }
  }, [categories]);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(newExpanded)));
  };

  // Group entries by category
  const entriesByCategory = entries.reduce((acc, entry) => {
    const cat = entry.category || 'Allgemein';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(entry);
    return acc;
  }, {} as Record<string, TeamKnowledgeBaseEntry[]>);

  // Get recent entries
  const recentEntries = [...entries]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 5);

  // Get starred entries
  const starredEntries = entries.filter(e => e.is_pinned);

  return (
    <>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-1">
          {/* Overview */}
          <NavItem
            icon={Home}
            label="Übersicht"
            isActive={!selectedCategory && !selectedEntryId}
            onClick={() => onCategorySelect(null)}
          />

          {/* Create New Button */}
          {canEdit && (
            <Button
              onClick={onCreateNew}
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-[#0052CC] dark:text-primary hover:bg-[#DEEBFF] dark:hover:bg-primary/10"
            >
              <Plus className="h-4 w-4" />
              <span>Neue Seite</span>
            </Button>
          )}

          <Separator className="my-3" />

          {/* Starred */}
          {starredEntries.length > 0 && (
            <NavSection title="Favoriten" icon={Star} defaultOpen>
              {starredEntries.map(entry => (
                <PageItem
                  key={entry.id}
                  title={entry.title}
                  isActive={entry.id === selectedEntryId}
                  onClick={() => onEntrySelect(entry.id)}
                  isStarred
                />
              ))}
            </NavSection>
          )}

          {/* Recent */}
          {recentEntries.length > 0 && (
            <NavSection title="Zuletzt bearbeitet" icon={Clock} defaultOpen>
              {recentEntries.map(entry => (
                <PageItem
                  key={entry.id}
                  title={entry.title}
                  isActive={entry.id === selectedEntryId}
                  onClick={() => onEntrySelect(entry.id)}
                  isStarred={entry.is_pinned}
                />
              ))}
            </NavSection>
          )}

          <Separator className="my-3" />

          {/* Page Tree by Category */}
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2">
            Seiten
          </div>
          
          {Object.entries(entriesByCategory).map(([category, categoryEntries]) => (
            <div key={category}>
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center gap-1 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors"
              >
                {expandedCategories.has(category) ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <Folder className="h-4 w-4 text-[#0052CC]/60 dark:text-primary/60" />
                <span className="font-medium text-foreground truncate">{category}</span>
                <span className="ml-auto text-xs text-muted-foreground bg-muted rounded px-1.5">
                  {categoryEntries.length}
                </span>
              </button>
              
              {expandedCategories.has(category) && (
                <div className="ml-5 mt-0.5 space-y-0.5 border-l-2 border-[#DEEBFF] dark:border-primary/20 pl-2">
                  {categoryEntries.map(entry => (
                    <PageItem
                      key={entry.id}
                      title={entry.title}
                      isActive={entry.id === selectedEntryId}
                      onClick={() => onEntrySelect(entry.id)}
                      isStarred={entry.is_pinned}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="p-2 border-t">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-muted-foreground"
        >
          <Settings className="h-4 w-4" />
          <span>Einstellungen</span>
        </Button>
      </div>
    </>
  );
};

// Nav Item Component
interface NavItemProps {
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const NavItem = ({ icon: Icon, label, isActive, onClick }: NavItemProps) => (
  <button
    onClick={onClick}
    className={cn(
      "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors",
      isActive
        ? "bg-[#DEEBFF] text-[#0052CC] dark:bg-primary/10 dark:text-primary font-medium"
        : "text-foreground hover:bg-muted"
    )}
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
  </button>
);

// Nav Section Component
interface NavSectionProps {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const NavSection = ({ title, icon: Icon, children, defaultOpen = true }: NavSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <Icon className="h-3 w-3" />
        <span>{title}</span>
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
};

// Page Item Component
interface PageItemProps {
  title: string;
  isActive: boolean;
  onClick: () => void;
  isStarred?: boolean;
}

const PageItem = ({ title, isActive, onClick, isStarred }: PageItemProps) => (
  <button
    onClick={onClick}
    className={cn(
      "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors text-left",
      isActive
        ? "bg-[#DEEBFF] text-[#0052CC] dark:bg-primary/10 dark:text-primary font-medium"
        : "text-foreground hover:bg-muted"
    )}
  >
    <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
    <span className="truncate flex-1">{title}</span>
    {isStarred && (
      <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />
    )}
  </button>
);

export default ConfluenceNavSidebar;
